/* eslint-disable max-lines-per-function */
import { buttonVariants, Button } from '@e2n/ui';
import { cn } from '@/lib/utils';
import { faArrowLeft, faPlusLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useTerminKalenderList } from '@/domain/termine/hooks/useTerminKalenderList';
import { OHNE_KALENDER_ID } from '@/domain/termine/shared/utils/defaultOhneKalender';
import { KalenderTable } from '@/domain/termine/features/TerminKalender/Desktop/Table';
import { getKalenderTableColumns } from '@/domain/termine/features/TerminKalender/Desktop/Table/KalenderTableColumns';
import { CreateTerminKalenderSheet } from '@/domain/termine/features/CreateTerminKalender/Sheet/CreateTerminKalenderSheet';
import { KalenderDeleteDialog } from '@/domain/termine/features/TerminKalender/Desktop/DeleteDialog/KalenderDeleteDialog';
import { useKalenderDeleteHandler } from '@/domain/termine/features/TerminKalender/shared/useKalenderDeleteHandler';
import { TerminKalender } from '@/domain/termine/shared/types/TerminKalender';

function KalenderDesktopHeader() {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 items-start justify-between w-full">
      <div className="flex items-center gap-2">
        <Link
          to="/termine/uebersicht"
          aria-label={t('domain.termine.title')}
          title={t('domain.termine.title')}
          className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }))}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </Link>
        <span className="font-semibold text-sm">
          {t('domain.termine.title')}
        </span>
      </div>
    </div>
  );
}

export function KalenderDesktopContent() {
  const { t } = useTranslation();
  const { kalender } = useTerminKalenderList();
  const filteredData = kalender.filter(
    (row) => row.kalenderSchluessel !== -1 && row.id !== OHNE_KALENDER_ID,
  );
  const [sheetOpen, setSheetOpen] = useState(false);

  const [deleteKalender, setDeleteKalender] = useState<TerminKalender | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { handleDelete, loading } = useKalenderDeleteHandler(kalender);

  const handleTableDelete = (
    kalender: TerminKalender,
    termineCount: number,
  ) => {
    if (termineCount === 0) {
      handleDelete(kalender);
    } else {
      setDeleteKalender(kalender);
      setIsDeleteDialogOpen(true);
    }
  };

  const columns = getKalenderTableColumns(t, handleTableDelete);

  return (
    <>
      <KalenderDesktopHeader />
      <div className="w-full">
        <div className="flex items-center justify-between mb-5">
          <span className="text-xl font-bold">
            {t('domain.termine.createTitle')}
          </span>
          <Button
            variant="default"
            size="icon"
            onClick={() => setSheetOpen(true)}>
            <FontAwesomeIcon icon={faPlusLarge} className="font-bold" />
          </Button>
        </div>
        <div className="mt-5">
          <KalenderTable columns={columns} data={filteredData} />
        </div>
      </div>

      <CreateTerminKalenderSheet open={sheetOpen} onOpenChange={setSheetOpen} />
      {deleteKalender && (
        <KalenderDeleteDialog
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            setIsDeleteDialogOpen(open);
            if (!open) setDeleteKalender(null);
          }}
          kalender={deleteKalender}
          otherKalender={kalender.filter((k) => k.id !== deleteKalender?.id)}
          onConfirm={async (idTargetTerminkalender) => {
            await handleDelete(deleteKalender, idTargetTerminkalender);
            setIsDeleteDialogOpen(false);
            setDeleteKalender(null);
          }}
          loading={loading}
        />
      )}
    </>
  );
}
